import type * as babylon from "@babylonjs/core";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";

import * as components from "../../components";
import { BabylonState } from "../../state";

/**
 * Fetches geometry for any model-backed mesh whose load has gone stale.
 *
 * The procedural primitives are built inline by `updateMesh`; the two variants
 * that come out of a file need it fetched and parsed first, so only they appear
 * here. A `model_node` takes one node's geometry, a `model` a whole posable copy
 * of the file — both served from the loader's per-file template cache, so a
 * panel drawing one model from several entities fetches it once.
 *
 * A mesh transitions `stale → loading → loaded` once and is not returned to
 * `stale` by a failure, so a node name that matches nothing is attempted once
 * rather than re-fetched every 50ms.
 */
export function reloadStaleMeshes(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    const mesh = entity.mesh;
    if (
      !mesh ||
      (mesh.spec.type !== "model_node" && mesh.spec.type !== "model") ||
      mesh.load?.state !== "stale"
    ) {
      continue;
    }
    // Cancel any load left over from a previous spec on this component.
    mesh.load.abortController.abort();

    mesh.load.state = "loading";
    const abortController = new AbortController();
    mesh.load.abortController = abortController;
    const { file } = mesh.spec;
    const label =
      mesh.spec.type === "model_node"
        ? `${file.relativePath ?? file.fileId}#${mesh.spec.nodeName}`
        : file.relativePath ?? file.fileId;

    const loading =
      mesh.spec.type === "model_node"
        ? loadNode(
            mesh,
            state,
            file.fileId,
            mesh.spec.nodeName,
            abortController.signal,
          )
        : loadModel(mesh, state, file.fileId, abortController.signal);

    loading.catch((e: unknown) => {
      if (abortController.signal.aborted && isAbortError(e)) {
        // Superseded or torn down; whatever replaced it owns the state now.
        return;
      }
      const error = e instanceof Error ? e : new Error(String(e));
      mesh.load = { state: "loaded", abortController };
      mesh.error = error;
      state.context.callbacks.onModelLoadFailed(label, error);
      ErrorMonitoringService.captureError(error);
    });
  }
}

/**
 * Attaches one node's geometry to the mesh component.
 *
 * Takes the component rather than reaching back through the state by entity id,
 * so a spec replaced during the async gap cannot have this load's result written
 * onto it — the same reasoning as `reloadPositions`.
 */
async function loadNode(
  mesh: components.Mesh,
  state: BabylonState,
  fileId: string,
  nodeName: string,
  signal: AbortSignal,
) {
  const clone = await state.context.modelLoader.cloneNode(
    fileId,
    nodeName,
    state.context.scene,
    signal,
  );

  if (signal.aborted) {
    // The abort landed while the asset was parsing. Nothing else holds this
    // clone, so release it or its geometry stays in the scene for good.
    clone.dispose();
    signal.throwIfAborted();
  }

  mesh.mesh = clone;
  markLoaded(mesh, state);
}

/**
 * Attaches a whole posable copy of the file to the mesh component.
 *
 * The copy's roots are parented to the component's anchor and the anchor alone
 * is placed, so the glTF-to-Babylon handedness conversion Babylon put on
 * `__root__` stays where it is. Writing TRS onto a root instead would replace
 * that conversion rather than compose with it, and lay the model on its side.
 *
 * `nodesByName` indexes the copy the way the file names it, taking the *first*
 * node of a repeated name — glTF does not require unique node names, and a joint
 * naming an ambiguous one can only be resolved arbitrarily. `__root__` is not
 * indexed: `getDescendants` starts below it, and it is Babylon's node rather
 * than one of the file's.
 */
async function loadModel(
  mesh: components.Mesh,
  state: BabylonState,
  fileId: string,
  signal: AbortSignal,
) {
  const instance = await state.context.modelLoader.instantiate(
    fileId,
    state.context.scene,
    signal,
  );

  const model = mesh.model;
  if (signal.aborted || !model) {
    // The abort landed while the asset was parsing, or the component was
    // replaced outright. Nothing else holds this copy, so release it or its
    // nodes stay in the scene for good.
    instance.dispose();
    signal.throwIfAborted();
    return;
  }

  model.instance = instance;
  model.nodesByName.clear();
  for (const root of instance.rootNodes) {
    root.parent = model.anchor;
    for (const node of root.getDescendants(false)) {
      if (!model.nodesByName.has(node.name)) {
        // Every node kind a model can hold — `Mesh`, `InstancedMesh`,
        // `TransformNode` — is a `TransformNode`; `getDescendants` is typed for
        // the general case and declares the looser `Node`.
        model.nodesByName.set(node.name, node as babylon.TransformNode);
      }
    }
  }

  markLoaded(mesh, state);
}

/** Shared tail: the component is serviced, and the scene's extent has grown. */
function markLoaded(mesh: components.Mesh, state: BabylonState) {
  mesh.load = { state: "loaded", abortController: new AbortController() };
  delete mesh.error;

  // The scene has geometry it did not have before, so the cached third-person
  // zoom envelope no longer reflects its extent.
  state.sceneConfig.entityGeneration =
    (state.sceneConfig.entityGeneration ?? 0) + 1;
}
